import type { Feature, FeatureCollection, Polygon } from "geojson";
import type { MapTool, MapClickEvent } from "../../../application/map/tools/types";
import type { ToolDeps } from "./types";
import { parsePolygonId, geoJSONKeyForType } from "../../helpers/JobMap/editHelpers";
import { mergeHolesUseCase } from "../../../application/map/useCases/mergePolygonsUseCase";

export function makeMergeHolesTool(deps: ToolDeps): MapTool {
	let firstFeatureId: string | null = null;

	function resolvePolygonFeature(
		featureId: string,
	): { feature: Feature<Polygon>; typePrefix: string; jobId: string } | null {
		const parsed = parsePolygonId(featureId);
		if (!parsed) return null;

		const jobId = deps.getSelectedJobId() ?? parsed.jobId;
		const geoKey = geoJSONKeyForType(parsed.typePrefix);
		const fc =
			geoKey === "boundaryGeoJSON"
				? deps.getBoundaryFC(jobId)
				: geoKey === "zonesGeoJSON"
					? deps.getZonesFC(jobId)
					: null;

		if (!fc) return null;
		const feature = fc.features[parsed.featureIndex];
		if (!feature || feature.geometry.type !== "Polygon") return null;

		return { feature: feature as Feature<Polygon>, typePrefix: parsed.typePrefix, jobId };
	}

	return {
		id: "merge-holes",
		label: "Merge Holes",
		cursor: "pointer",
		onActivate() {
			firstFeatureId = null;
		},
		onFeatureClick(featureId: string, _e: MapClickEvent) {
			if (!firstFeatureId) {
				const resolved = resolvePolygonFeature(featureId);
				if (!resolved) {
					deps.showError("Could not resolve polygon");
					return;
				}
				firstFeatureId = featureId;
				deps.toolOverlay.highlightFeature(resolved.feature);
				return;
			}

			const firstResolved = resolvePolygonFeature(firstFeatureId);
			const secondResolved = resolvePolygonFeature(featureId);

			if (!firstResolved || !secondResolved) {
				deps.showError("Could not resolve polygons");
				firstFeatureId = null;
				deps.toolOverlay.clearOverlay();
				return;
			}

			const applyFC = firstResolved.typePrefix === "boundary" ? deps.applyBoundary : deps.applyZones;

			const result = mergeHolesUseCase(
				firstResolved.feature,
				secondResolved.feature,
				(merged) => {
					const fc: FeatureCollection = { type: "FeatureCollection", features: [merged] };
					applyFC(firstResolved.jobId, fc);
				},
				(a, b) => {
					const fc: FeatureCollection = { type: "FeatureCollection", features: [a, b] };
					applyFC(firstResolved.jobId, fc);
				},
			);

			if (!result.ok) {
				deps.showError(result.error);
			} else {
				deps.undoStack.push(result.data.command);
			}

			firstFeatureId = null;
			deps.toolOverlay.clearOverlay();
		},
		onDeactivate() {
			firstFeatureId = null;
			deps.toolOverlay.clearOverlay();
		},
	};
}
